import { z } from 'zod';
import type { CreateCouponDto, QueryCouponsDto, UpdateCouponDto } from '@control-center/shared';

import { apiClient } from './api-client';

// Resposta do backend (CouponView do coupons.service).
const couponViewSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  description: z.string().nullable(),
  discountType: z.enum(['percent', 'fixed']),
  discountValue: z.number(),
  maxDiscount: z.number().nullable(),
  appliesToAllPlans: z.boolean(),
  planIds: z.array(z.string()),
  isActive: z.boolean(),
  usageLimit: z.number().nullable(),
  usageCount: z.number(),
  validFrom: z.string().nullable(),
  validUntil: z.string().nullable(),
  minPurchase: z.number().nullable(),
  status: z.enum(['active', 'inactive', 'expired', 'exhausted', 'scheduled']),
  discountDurationMonths: z.number().nullable(),
  createdAt: z.string(),
});
export type CouponView = z.infer<typeof couponViewSchema>;
export type CouponDerivedStatus = CouponView['status'];

const listResponseSchema = z.object({
  items: z.array(couponViewSchema),
  meta: z.object({
    next_cursor: z.string().nullable(),
    total_estimate: z.number().nullable(),
  }),
});
const singleResponseSchema = z.object({ data: couponViewSchema });

export interface CouponListResponse {
  items: CouponView[];
  meta: { next_cursor: string | null; total_estimate: number | null };
}

export async function fetchCoupons(
  params: QueryCouponsDto = { limit: 50 },
): Promise<CouponListResponse> {
  const { data } = await apiClient.get('/api/coupons', { params });
  return listResponseSchema.parse(data);
}

export async function createCoupon(payload: CreateCouponDto): Promise<CouponView> {
  const { data } = await apiClient.post('/api/coupons', payload);
  return singleResponseSchema.parse(data).data;
}

export async function updateCoupon(id: string, payload: UpdateCouponDto): Promise<CouponView> {
  const { data } = await apiClient.patch(`/api/coupons/${id}`, payload);
  return singleResponseSchema.parse(data).data;
}

export async function deleteCoupon(id: string): Promise<void> {
  await apiClient.delete(`/api/coupons/${id}`);
}

// -----------------------------------------------------------------------------
// Resgates — quem usou o cupom, quem fechou e quanto custou
// -----------------------------------------------------------------------------

const redemptionSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(['reserved', 'confirmed', 'released']),
  clientId: z.string(),
  clientName: z.string().nullable(),
  clientEmail: z.string().nullable(),
  subscriptionId: z.string().nullable(),
  originalPrice: z.number(),
  discountApplied: z.number(),
  finalPrice: z.number(),
  usedAt: z.string(),
  confirmedAt: z.string().nullable(),
});

const redemptionsResponseSchema = z.object({
  items: z.array(redemptionSchema),
  summary: z.object({
    attempts: z.number(),
    confirmed: z.number(),
    pending: z.number(),
    abandoned: z.number(),
    discountGiven: z.number(),
    revenue: z.number(),
    /** `null` quando ninguém usou ainda — 0% diria "fracassou". */
    conversionRate: z.number().nullable(),
  }),
});

export type CouponRedemption = z.infer<typeof redemptionSchema>;
export type CouponRedemptions = z.infer<typeof redemptionsResponseSchema>;

export async function fetchCouponRedemptions(couponId: string): Promise<CouponRedemptions> {
  const { data } = await apiClient.get(`/api/coupons/${couponId}/redemptions`);
  return redemptionsResponseSchema.parse(data);
}
